import { useEffect } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogOverlay } from "@/components/ui/dialog";
import { ImprovedFooter } from "./ImprovedFooter";
import { Button } from "@/components/ui/button";

interface ShowData {
  id: number;
  name: string;
  subtitle: string;
  startTime: string;
  duration: string;
  price: number;
  currency: string;
  minimumCharge: number;
  availableLanguages: string[];
  groupSize: string;
  status?: string;
}

interface FullscreenVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
  show: ShowData;
  date: Date;
  onBookNow: () => void;
}

const getGroupSizeNumbers = (groupSize: string) => {
  const match = groupSize.match(/(\d+)-(\d+)/);
  if (match) {
    const min = parseInt(match[1]);
    const max = parseInt(match[2]);
    return { min, max, avg: Math.ceil((min + max) / 2) };
  }
  return { min: 1, max: 1, avg: 1 };
};

export function FullscreenVideoModal({
  isOpen,
  onClose,
  videoId,
  show,
  date,
  onBookNow,
}: FullscreenVideoModalProps) {
  const groupNumbers = getGroupSizeNumbers(show.groupSize);
  const pricePerPerson = Math.round(show.price / groupNumbers.avg);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogOverlay className="bg-black/95 backdrop-blur-sm animate-in fade-in-0 duration-300" />
      <DialogContent className="max-w-none w-full h-full p-0 border-0 bg-transparent focus-visible:outline-none animate-in fade-in-0 zoom-in-95 duration-300 grid grid-rows-[1fr_auto]">
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 bg-black/20 text-white hover:bg-black/40 h-10 w-10 transition-all duration-200 animate-in fade-in-0 slide-in-from-top-2 delay-150"
          onClick={onClose}
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Video Container - Takes available space */}
        <div className="flex items-center justify-center px-4 py-16 overflow-hidden">
          <div className="w-full max-w-6xl aspect-video animate-in fade-in-0 zoom-in-95 duration-500 delay-200">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&si=QVV1zaHM3amfrgTL`}
              title={show.name}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="rounded-lg shadow-2xl"
            />
          </div>
        </div>

        {/* Footer - Takes natural height */}
        <div className="bg-black/80 text-white backdrop-blur-sm border-t border-white/20 p-4 sm:p-6 animate-in slide-in-from-bottom-4 duration-400 delay-300">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-1">
                {show.name}
              </h3>
              <p className="text-sm text-gray-300">{show.subtitle}</p>
            </div>

            <ImprovedFooter
              date={date}
              startTime={show.startTime}
              minimumCharge={show.minimumCharge}
              currency={show.currency}
              availableLanguages={show.availableLanguages}
              pricePerPerson={pricePerPerson}
              duration={show.duration}
              groupSize={show.groupSize}
              isSoldOut={show.status === "soldout"}
              onBookNow={onBookNow}
              className="text-white [&_*]:text-white [&_.text-muted-foreground]:text-gray-300"
            />
          </div>
        </div>

        {/* Click outside to close - invisible overlay */}
        <div
          className="absolute inset-0 -z-10"
          onClick={onClose}
          onTouchStart={(e) => {
            // Handle touch events for mobile
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          aria-label="Close modal"
        />
      </DialogContent>
    </Dialog>
  );
}
